
import { useEffect, useRef, useState } from 'react';

export default function Services() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    const el = sectionRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, []);

  const services = [
    {
      title: 'Commercial & Corporate Law',
      description: 'Expert guidance on business formations, contracts, mergers, and corporate governance.',
      image: 'https://public.readdy.ai/ai/img_res/baafc32a46247e92da0814f4baf398d0.jpg',
    },
    {
      title: 'Employment Law',
      description: 'Comprehensive support for workplace disputes, contracts, and labor relations.',
      image: 'https://readdy.ai/api/search-image?query=professional%20office%20consultation%20room%2C%20two%20chairs%20facing%20each%20other%20across%20a%20desk%2C%20legal%20documents%20on%20table%2C%20warm%20neutral%20lighting%2C%20modern%20minimalist%20interior%2C%20corporate%20professional%20setting%2C%20no%20people%2C%20clean%20and%20organized&width=480&height=360&seq=service-employment-law-001&orientation=landscape',
    },
    {
      title: 'Family Law',
      description: 'Compassionate assistance with divorce, custody, maintenance, and family matters.',
      image: 'https://readdy.ai/api/search-image?query=calm%20and%20warm%20legal%20consultation%20office%20with%20soft%20lighting%2C%20comfortable%20seating%2C%20neutral%20beige%20and%20white%20tones%2C%20potted%20plant%2C%20wooden%20desk%2C%20professional%20yet%20welcoming%20atmosphere%2C%20no%20people%2C%20family%20law%20setting&width=480&height=360&seq=service-family-law-001&orientation=landscape',
    },
    {
      title: 'Debt Collection',
      description: 'Efficient recovery solutions for outstanding debts and financial disputes.',
      image: 'https://readdy.ai/api/search-image?query=financial%20documents%20and%20legal%20papers%20on%20a%20clean%20desk%2C%20calculator%2C%20pen%2C%20professional%20finance%20and%20law%20office%20setting%2C%20neutral%20white%20and%20grey%20tones%2C%20modern%20minimalist%20workspace%2C%20no%20people%2C%20high%20contrast%20lighting&width=480&height=360&seq=service-debt-collection-001&orientation=landscape',
    },
    {
      title: 'Deceased Estates & Wills',
      description: 'Professional estate planning, will drafting, and estate administration services.',
      image: 'https://readdy.ai/api/search-image?query=elegant%20law%20office%20with%20leather-bound%20books%20on%20shelves%2C%20antique%20wooden%20desk%2C%20formal%20legal%20documents%2C%20classic%20professional%20interior%2C%20warm%20amber%20lighting%2C%20sophisticated%20estate%20planning%20setting%2C%20no%20people&width=480&height=360&seq=service-estates-wills-001&orientation=landscape',
    },
    {
      title: 'RAF Claims',
      description: 'Dedicated representation for Road Accident Fund claims and compensation.',
      image: 'https://readdy.ai/api/search-image?query=empty%20road%20stretching%20into%20distance%2C%20dramatic%20sky%2C%20South%20African%20highway%20landscape%2C%20wide%20angle%20photography%2C%20cinematic%20moody%20lighting%2C%20no%20vehicles%20or%20people%2C%20road%20accident%20fund%20legal%20context&width=480&height=360&seq=service-raf-claims-001&orientation=landscape',
    },
    {
      title: 'Mining Law',
      description: 'Specialized legal services for mining rights, compliance, and industry regulations.',
      image: 'https://readdy.ai/api/search-image?query=aerial%20view%20of%20open%20pit%20mine%20in%20South%20Africa%2C%20dramatic%20landscape%2C%20industrial%20mining%20operations%2C%20earth%20tones%20brown%20and%20orange%2C%20professional%20documentary%20photography%2C%20no%20people%20visible%2C%20wide%20angle&width=480&height=360&seq=service-mining-law-001&orientation=landscape',
    },
    {
      title: 'Criminal Law',
      description: 'Strong defense representation for criminal charges and legal proceedings.',
      image: 'https://readdy.ai/api/search-image?query=formal%20courtroom%20interior%20with%20wooden%20benches%2C%20judge%20podium%2C%20South%20African%20court%20setting%2C%20dramatic%20lighting%20through%20high%20windows%2C%20empty%20courtroom%2C%20professional%20legal%20atmosphere%2C%20no%20people%2C%20classic%20architecture&width=480&height=360&seq=service-criminal-law-001&orientation=landscape',
    },
  ];

  return (
    <section id="practice-areas" ref={sectionRef} className="relative pt-10 pb-20 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12">
        {/* Header */}
        <div
          className={`text-center mb-10 md:mb-14 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#6A1B9A] mb-3">What We Do</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2C2C] mb-3 md:mb-4">
            Our Practice Areas
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-500 max-w-2xl mx-auto">
            Comprehensive legal services tailored to meet your unique needs across multiple practice areas
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              className={`group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-400 cursor-pointer ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 0.07}s` }}
            >
              {/* Image */}
              <div className="relative w-full h-44 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                {/* Subtle dark overlay */}
                <div className="absolute inset-0 bg-black/15"></div>
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="text-sm md:text-base font-bold text-[#2C2C2C] mb-2 leading-snug group-hover:text-[#6A1B9A] transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                  {service.description}
                </p>
              </div>

              {/* Bottom accent bar */}
              <div className="h-0.5 w-0 group-hover:w-full bg-[#6A1B9A] transition-all duration-400 mx-0"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
